import React from 'react';
import { ArrowUpRight, Car, Disc3 } from 'lucide-react';
import RadioPlayer from '@/components/RadioPlayer';
import DonationBanner from '@/components/DonationBanner';
import NowPlayingCard from '@/components/NowPlayingCard';
import PWASpotlight from '@/components/PWASpotlight';
import ListenEverywhere from '@/components/ListenEverywhere';
import { HOME } from '@/constants/testIds';
import { STREAMS, STATUS, PWA, LOGOS } from '@/constants/stations';

const MAJESTIC_QUALITIES = [
  { id: 'hd',  label: 'HD',  url: STREAMS.majesticHD,  kbps: 320 },
  { id: 'aac', label: 'AAC', url: STREAMS.majesticAAC, kbps: 'AAC' },
];

const Hero = () => (
  <section data-testid={HOME.hero} className="relative min-h-[100svh] flex items-center pt-28 pb-16 overflow-hidden">
    {/* Background image */}
    <div className="absolute inset-0 z-0">
      <img
        src="https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?auto=format&fit=crop&w=2000&q=80"
        alt=""
        className="w-full h-full object-cover opacity-40"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-vinyl/85 via-vinyl/70 to-vinyl" />
      <div className="absolute inset-0 bg-gradient-to-r from-vinyl via-transparent to-vinyl/80" />
    </div>

    <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 w-full">
      <div className="grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber/30 bg-amber/5 mb-7">
            <Disc3 className="w-3.5 h-3.5 text-amber" />
            <span className="text-[10px] tracking-[0.25em] uppercase text-amber font-bold">
              Independent · Commercial Free · HD
            </span>
          </div>

          <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] text-cream leading-[0.95] tracking-tight">
            The golden age of radio,
            <br />
            <span className="text-amber italic font-serif-italic">alive today.</span>
          </h1>

          <p className="mt-7 text-base md:text-lg text-cream/70 font-body leading-relaxed max-w-xl">
            Forty&apos;s through Eighty&apos;s hits — easy listening, oldies, jazz, swing, rock and roll,
            blues, country, doo wop and big band. Only the cleanest version of every record,
            streaming in 320 kbps, around the clock.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm font-serif-italic text-amber-light/80">
            <span>· 40s</span><span>· 50s</span><span>· 60s</span>
            <span>· 70s</span><span>· 80s</span>
            <span>· Oldies</span><span>· Swing</span><span>· Doo Wop</span>
          </div>
        </div>

        <div className="lg:col-span-5">
          <RadioPlayer
            qualities={MAJESTIC_QUALITIES}
            title="Majestic Jukebox Radio"
            subtitle="Switch HD MP3 or AAC — both 24/7 live"
            logo={LOGOS.majestic}
            testIdPlay="majestic-player-play-button"
            testIdPause="majestic-player-pause-button"
            testIdVolume="majestic-player-volume-slider"
            testIdQuality="majestic-quality"
            variant="primary"
          />
        </div>
      </div>
    </div>
  </section>
);

const DiscoverGrid = () => (
  <section className="relative py-20 md:py-28">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <div className="flex items-end justify-between gap-6 mb-10">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-amber font-bold mb-3">
            Discover · Tune in · Explore
          </div>
          <h2 className="font-display text-4xl md:text-5xl text-cream leading-tight">
            Two stations. <span className="font-serif-italic italic text-amber">One golden hour.</span>
          </h2>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {/* Jazz Razzmatazz secondary radio */}
        <div className="md:col-span-7">
          <RadioPlayer
            streamUrl={STREAMS.jazz}
            title="Jazz Razzmatazz Radio"
            subtitle="Our partner station · Wonderful Jazz"
            logo={LOGOS.jazz}
            testIdPlay="jazz-player-play-button"
            testIdPause="jazz-player-pause-button"
            testIdVolume="jazz-player-volume-slider"
            variant="secondary"
          />
          <div className="mt-3 text-right">
            <a
              data-testid={HOME.jazzExternalLink}
              href="https://jazzrazzmatazzradio.netlify.app/"
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-amber-light/80 hover:text-amber font-body"
            >
              Open Jazz Razzmatazz player <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>

        {/* CarPlay card */}
        <a
          data-testid={HOME.carplayLink}
          href="https://majesticjukeboxradio.netlify.app/"
          target="_blank" rel="noopener noreferrer"
          className="md:col-span-5 group relative overflow-hidden rounded-3xl border border-amber/20 bg-espresso min-h-[280px] flex flex-col justify-end hover:-translate-y-1 transition-transform"
        >
          <img
            src="https://images.unsplash.com/photo-1583121274602-3e2820c69888?auto=format&fit=crop&w=1400&q=80"
            alt="Vintage car dashboard"
            className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-60 group-hover:scale-105 transition-all duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-vinyl via-vinyl/70 to-transparent" />

          <div className="relative p-6 md:p-8">
            <div className="flex items-center gap-2 mb-3">
              <Car className="w-4 h-4 text-amber" />
              <span className="text-[10px] tracking-[0.25em] uppercase text-amber font-bold">CarPlay edition</span>
            </div>
            <div className="font-display text-2xl md:text-3xl text-cream leading-tight">
              Take us for a drive.
            </div>
            <p className="mt-2 text-sm text-cream/70 font-body max-w-sm">
              Open the dedicated player — designed for your car&apos;s CarPlay system. Tap to launch ↗
            </p>
            <span className="absolute top-6 right-6 w-10 h-10 rounded-full border border-amber/40 flex items-center justify-center text-amber group-hover:bg-amber group-hover:text-vinyl transition">
              <ArrowUpRight className="w-4 h-4" />
            </span>
          </div>
        </a>
      </div>
    </div>
  </section>
);

const QuoteStrip = () => (
  <section className="relative border-y border-amber/10 bg-espresso/40">
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-14 text-center">
      <Disc3 className="w-7 h-7 text-amber mx-auto mb-5" />
      <p className="font-serif-italic italic text-cream/85 text-xl md:text-2xl leading-relaxed">
        “Music expresses that which cannot be said and on which it is impossible to be silent.”
      </p>
      <div className="mt-4 text-[10px] tracking-[0.25em] uppercase text-amber font-bold">
        — Victor Hugo
      </div>
    </div>
  </section>
);

const NowPlayingMini = () => (
  <section className="relative py-14 md:py-20">
    <div className="max-w-5xl mx-auto px-6 md:px-10">
      <div className="text-center mb-8">
        <div className="text-[10px] tracking-[0.25em] uppercase text-amber font-bold mb-3">
          On Air right now
        </div>
        <h2 className="font-display text-3xl md:text-4xl text-cream leading-tight">
          What&apos;s spinning <span className="font-serif-italic italic text-amber">live.</span>
        </h2>
      </div>

      <div className="grid grid-cols-2 gap-4 md:gap-6 max-w-2xl mx-auto">
        <NowPlayingCard
          stationName="Majestic Jukebox Radio"
          statusUrl={STATUS.majesticHD}
          pwaUrl={PWA.majestic}
          fallbackArt={LOGOS.majestic}
          testIdRoot="now-playing-majestic"
        />
        <NowPlayingCard
          stationName="Jazz Razzmatazz Radio"
          statusUrl={STATUS.jazz}
          pwaUrl={PWA.jazz}
          fallbackArt={LOGOS.jazz}
          testIdRoot="now-playing-jazz"
        />
      </div>
    </div>
  </section>
);

const Home = () => {
  return (
    <div>
      <Hero />

      <NowPlayingMini />

      {/* PWA spotlight — the value-first reciprocity build-up */}
      <PWASpotlight />

      {/* Donation Banner — soft, dignified, post-value */}
      <section className="relative py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <DonationBanner />
        </div>
      </section>

      {/* Listen Everywhere — Other Links + TuneIn partner + Socials */}
      <ListenEverywhere />

      <QuoteStrip />

      <DiscoverGrid />
    </div>
  );
};

export default Home;
